import { Sun, Moon, CloudSun, CloudMoon, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning } from "lucide-react";
import api from "@/lib/api";
import { logWarn } from "@/lib/log";

// Current weather + today's hourly for [lat, lng] via the backend Open-Meteo proxy (10-minute server cache).
export async function fetchWeather([lat, lng]) {
  try {
    const { data } = await api.get("/weather", { params: { lat, lng } });
    return data;
  } catch (e) {
    logWarn("weather fetch failed:", e);
    return null;
  }
}

// WMO weather codes → condition group.
export function conditionOf(code) {
  if (code === 0) return "clear";
  if (code === 1) return "mostlyClear";
  if (code === 2) return "partlyCloudy";
  if (code === 3) return "overcast";
  if (code === 45 || code === 48) return "fog";
  if (code >= 51 && code <= 57) return "drizzle";
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return "rain";
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return "snow";
  if (code >= 95) return "thunder";
  return "overcast";
}

export const CONDITION_LABEL = {
  clear: { id: "Cerah", en: "Clear", ar: "صحو" },
  mostlyClear: { id: "Cerah berawan", en: "Mainly clear", ar: "صحو غالبًا" },
  partlyCloudy: { id: "Berawan sebagian", en: "Partly cloudy", ar: "غائم جزئيًا" },
  overcast: { id: "Mendung", en: "Overcast", ar: "غائم" },
  fog: { id: "Berkabut", en: "Fog", ar: "ضباب" },
  drizzle: { id: "Gerimis", en: "Drizzle", ar: "رذاذ" },
  rain: { id: "Hujan", en: "Rain", ar: "مطر" },
  snow: { id: "Salju", en: "Snow", ar: "ثلج" },
  thunder: { id: "Badai petir", en: "Thunderstorm", ar: "عاصفة رعدية" },
};

export function conditionIcon(cond, isDay = true) {
  switch (cond) {
    case "clear": return isDay ? Sun : Moon;
    case "mostlyClear":
    case "partlyCloudy": return isDay ? CloudSun : CloudMoon;
    case "fog": return CloudFog;
    case "drizzle": return CloudDrizzle;
    case "rain": return CloudRain;
    case "snow": return CloudSnow;
    case "thunder": return CloudLightning;
    default: return Cloud;
  }
}

// Heat tiers from apparent ("feels like") temperature and UV index — thresholds follow common heat-stress
// guidance used for pilgrims in Makkah/Madinah.
export const HEAT_TIERS = [
  {
    id: "extreme", min: 42, uv: 11,
    label: { id: "Bahaya panas ekstrem", en: "Extreme heat danger", ar: "خطر حرارة شديدة" },
    tips: [
      { id: "Minum 200–250 ml air setiap 20–30 menit meski tidak haus; tambahkan oralit/kurma untuk elektrolit.", en: "Drink 200–250 ml of water every 20–30 minutes even when not thirsty; add electrolytes (ORS) or dates.", ar: "اشرب 200–250 مل من الماء كل 20–30 دقيقة حتى بدون عطش، وأضف الأملاح أو التمر." },
      { id: "Tunda tawaf/sa'i dan ziarah luar ruangan antara pukul 11.00–16.00; pilih malam atau setelah Subuh.", en: "Postpone tawaf/sa'i and outdoor visits between 11:00–16:00; go at night or after Fajr.", ar: "أجّل الطواف والسعي والزيارات الخارجية بين 11:00–16:00؛ اختر الليل أو بعد الفجر." },
      { id: "Gunakan payung, kacamata hitam, dan sandal; basahi kepala/leher dengan air zamzam bila perlu.", en: "Use an umbrella, sunglasses and sandals; wet your head/neck with water when needed.", ar: "استخدم المظلة والنظارات الشمسية والصنادل؛ بلّل رأسك وعنقك بالماء عند الحاجة." },
      { id: "Kenali gejala heat stroke: pusing, mual, kulit panas-kering, bingung — segera ke pos kesehatan terdekat.", en: "Know heat-stroke signs: dizziness, nausea, hot dry skin, confusion — go to the nearest clinic immediately.", ar: "اعرف أعراض ضربة الشمس: دوار، غثيان، جلد حار جاف، تشوش — توجّه فورًا لأقرب مركز صحي." },
    ],
  },
  {
    id: "veryHot", min: 37, uv: 8,
    label: { id: "Sangat panas", en: "Very hot", ar: "حار جدًا" },
    tips: [
      { id: "Targetkan 3–4 liter air sehari; bawa botol dan isi ulang dari keran zamzam.", en: "Aim for 3–4 litres of water a day; carry a bottle and refill from Zamzam taps.", ar: "استهدف 3–4 لترات من الماء يوميًا؛ احمل زجاجة وأعد ملأها من صنابير زمزم." },
      { id: "Hindari sengatan langsung pukul 11.00–15.00; pakai payung dan pakaian longgar berwarna terang.", en: "Avoid direct sun 11:00–15:00; use an umbrella and loose, light-coloured clothing.", ar: "تجنّب الشمس المباشرة بين 11:00–15:00؛ استخدم المظلة وملابس فضفاضة فاتحة." },
      { id: "Kurangi kafein dan minuman manis; perbanyak buah berair (semangka, kurma, jeruk).", en: "Cut caffeine and sugary drinks; eat water-rich fruit (watermelon, dates, oranges).", ar: "قلّل الكافيين والمشروبات السكرية؛ أكثر من الفواكه الغنية بالماء (البطيخ، التمر، البرتقال)." },
    ],
  },
  {
    id: "hot", min: 32, uv: 6,
    label: { id: "Panas", en: "Hot", ar: "حار" },
    tips: [
      { id: "Minum minimal 2–3 liter air sehari, sedikit-sedikit tapi sering.", en: "Drink at least 2–3 litres of water a day, little and often.", ar: "اشرب ما لا يقل عن 2–3 لترات من الماء يوميًا، بكميات قليلة ومتكررة." },
      { id: "Gunakan tabir surya dan payung saat berjalan ke masjid di siang hari.", en: "Use sunscreen and an umbrella when walking to the mosque at midday.", ar: "استخدم واقي الشمس والمظلة عند المشي إلى المسجد في منتصف النهار." },
    ],
  },
];

export function heatTier(apparent, uv) {
  if (apparent == null) return null;
  return HEAT_TIERS.find((t) => apparent >= t.min || (uv != null && uv >= t.uv)) || null;
}

// Hottest remaining hour today (by apparent temperature) from the hourly series.
export function peakToday(hourly, nowIso) {
  if (!hourly?.time?.length) return null;
  let best = null;
  hourly.time.forEach((t, i) => {
    if (nowIso && t < nowIso.slice(0, 13)) return;
    const a = hourly.apparent_temperature?.[i];
    if (a != null && (!best || a > best.apparent)) best = { time: t, apparent: a, temp: hourly.temperature_2m?.[i], uv: hourly.uv_index?.[i] };
  });
  return best;
}
